import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLines, Reveal, MaskLinesInView } from "../components/Reveal";
import { ArrowButton, ArrowLink } from "../components/Buttons";
import { Marquee } from "../components/Marquee";
import { LogoMarquee } from "../components/LogoMarquee";
import { SERVICES, PILLARS, IMG } from "../data/site";
import { ArrowUpRight } from "lucide-react";

const Home = () => {
  // Global scroll-based hero parallax (avoids target-container measurement warning under Lenis).
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 900], ["0%", "16%"]);
  const imgScale = useTransform(scrollY, [0, 900], [1.08, 1.2]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-bottle-green">
        <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
          <img src={IMG.hero} alt="Tara Creative Group campaign" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-bottle-green/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-bottle-green via-bottle-green/20 to-transparent" />
        </motion.div>

        <div className="relative z-10 max-w-[1500px] w-full mx-auto px-5 md:px-10 pb-16 md:pb-24 pt-40">
          <motion.span
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="label text-cream/70 flex items-center gap-3"
          >
            <span className="w-8 h-px bg-cream/50" /> Creative · Marketing · Branding
          </motion.span>

          <h1 className="mt-7 text-cream">
            <MaskLines
              lines={["We Connect.", "We Create.", "We Grow."]}
              delay={0.35}
              lineClass="font-serif text-6xl md:text-8xl lg:text-[10rem] tracking-tight leading-[0.86]"
            />
          </h1>

          <div className="grid md:grid-cols-12 gap-8 mt-10 items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
              className="md:col-span-6 font-sans text-base md:text-lg text-cream/80 leading-relaxed"
            >
              Tara Creative Group is a creative marketing and branding agency combining strategy, creativity and communication to build brands that connect, engage and grow.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
              className="md:col-span-6 md:col-start-7 flex flex-wrap gap-4 md:justify-end"
            >
              <ArrowButton to="/services" variant="light" testid="hero-explore-services">Explore Our Services</ArrowButton>
              <ArrowButton to="/contact" variant="ghostLight" testid="hero-work-together">Let's Work Together</ArrowButton>
            </motion.div>
          </div>
        </div>
      </section>

      <LogoMarquee />

      {/* WHAT WE DO */}
      <section data-testid="what-we-do" className="bg-cream py-24 md:py-36">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 md:gap-16 items-start">
            <div className="md:col-span-6">
              <Reveal><span className="label text-dark-text/50">What we do</span></Reveal>
              <MaskLinesInView
                lines={["From strategy to story.", "From story to growth."]}
                className="block mt-5"
                lineClass="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.98] text-dark-text"
              />
            </div>
            <Reveal delay={0.15} className="md:col-span-5 md:col-start-8">
              <p className="font-sans text-base md:text-lg text-dark-text/70 leading-relaxed">
                We combine strategic thinking, creative direction and marketing expertise to help businesses build distinctive brands and create meaningful connections with their audiences.
              </p>
              <ArrowLink to="/services" testid="what-we-do-all-services" className="mt-8">All Services</ArrowLink>
            </Reveal>
          </div>

          {/* Services list */}
          <div className="mt-16 md:mt-24 border-t border-dark-text/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.04}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`home-service-${s.slug}`}
                  className="group grid grid-cols-12 items-center gap-4 py-6 md:py-8 border-b border-dark-text/10 hover:bg-warm-beige/60 transition-colors duration-300 px-2 md:px-4"
                >
                  <span className="col-span-2 md:col-span-1 font-mono text-xs text-dark-text/45">{s.num}</span>
                  <span className="col-span-8 md:col-span-4 font-serif text-2xl md:text-3xl tracking-tight text-dark-text group-hover:translate-x-2 transition-transform duration-300">
                    {s.name}
                  </span>
                  <span className="hidden md:block md:col-span-5 font-sans text-sm text-dark-text/60 leading-relaxed">
                    {s.short}
                  </span>
                  <span className="col-span-2 md:col-span-2 flex justify-end">
                    <ArrowUpRight className="w-6 h-6 text-dark-text/40 group-hover:text-bottle-green group-hover:rotate-45 transition-all duration-300" strokeWidth={1.4} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee text="From Strategy to Story" testid="home-marquee" />

      {/* WHY TARA preview */}
      <section data-testid="why-tara-preview" className="bg-bottle-green text-cream py-24 md:py-36">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row justify-between md:items-end gap-8">
            <div>
              <Reveal><span className="label text-cream/50">Why Tara?</span></Reveal>
              <MaskLinesInView lines={["Why Tara?"]} className="block mt-5" lineClass="font-serif text-5xl md:text-7xl tracking-tight text-cream" />
            </div>
            <Reveal delay={0.2}>
              <ArrowLink to="/why-tara" light testid="why-tara-discover">Discover Why Tara</ArrowLink>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-px mt-16 bg-cream/12 border border-cream/12">
            {PILLARS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.08} className="bg-bottle-green p-8 md:p-8 min-h-[240px] flex flex-col justify-between hover:bg-secondary-green transition-colors duration-500">
                <span className="font-mono text-xs text-cream/50">{p.num}</span>
                <div>
                  <h3 className="font-serif text-xl md:text-2xl tracking-tight leading-tight">{p.title}</h3>
                  <p className="font-sans text-sm text-cream/60 leading-relaxed mt-3">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY TARA close CTA */}
      <section className="bg-cream py-24 md:py-36">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <span className="label text-dark-text/50">Let's build something</span>
            <p className="font-serif text-4xl md:text-6xl tracking-tight text-dark-text max-w-3xl mx-auto leading-[1.02] mt-6">
              Every great brand starts with a conversation.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <ArrowButton to="/services" testid="home-explore-services-bottom">Explore Our Services</ArrowButton>
              <ArrowButton to="/contact" variant="outline" testid="home-start-project-bottom">Start a Project</ArrowButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
